import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  validateEvent, 
  sanitizeURL, 
  sanitizePageTitle, 
  logSecurityEvent 
} from '../utils/analyticsSecurityUtils';

/**
 * Custom hook for Google Analytics 4 integration with privacy-first settings
 * Implements HIPAA-compliant configuration:
 * - IP anonymization enabled
 * - Advertising features disabled
 * - Ad personalization disabled
 * - Automatic page view tracking on route changes
 * - PHI detection and data sanitization
 * - Security audit logging
 */
export function useGoogleAnalytics() {
  const location = useLocation();
  const measurementId = import.meta.env?.VITE_GOOGLE_ANALYTICS_ID;

  useEffect(() => {
    // Skip if no measurement ID configured
    if (!measurementId || measurementId === 'your-google-analytics-id-here') {
      return;
    }

    // Check user consent
    const hasConsent = localStorage.getItem('analytics_consent') === 'true';
    if (!hasConsent) {
      logSecurityEvent('consent_required', { page: location?.pathname });
      return;
    }

    // Initialize Google Analytics only once
    if (!window.dataLayer) {
      // Load gtag.js script
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head?.appendChild(script);

      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      function gtag(...args) {
        window.dataLayer?.push(args);
      }
      window.gtag = gtag;
      
      // Initialize with timestamp
      gtag('js', new Date());
      
      // Configure with privacy-first settings (HIPAA-compliant)
      gtag('config', measurementId, {
        anonymize_ip: true,              // Anonymize IP addresses
        allow_google_signals: false,     // Disable advertising features
        allow_ad_personalization: false, // Disable ad personalization
        cookie_flags: 'SameSite=None;Secure', // Secure cookie settings
        cookie_expires: 63072000,        // 2 years (default)
        send_page_view: false,           // Manual page view tracking with sanitization
      });
      
      logSecurityEvent('analytics_initialized', { measurementId });
    }

    // Track page views on route changes with sanitization
    if (window.gtag) {
      const sanitizedPath = sanitizeURL(location?.pathname + location?.search);
      const sanitizedTitle = sanitizePageTitle(document.title);
      
      window.gtag('event', 'page_view', {
        page_path: sanitizedPath,
        page_location: window.location?.origin + sanitizedPath,
        page_title: sanitizedTitle,
      });
      
      logSecurityEvent('page_view_tracked', { 
        original_path: location?.pathname,
        sanitized_path: sanitizedPath,
      });
    }
  }, [location, measurementId]);
}

/**
 * Helper function to track custom events with PHI detection and sanitization
 * Always checks if gtag is available and validates data before tracking
 * 
 * @param {string} eventName - Name of the event (e.g., 'button_click', 'form_submit')
 * @param {object} eventParams - Additional parameters for the event
 * 
 * @example
 * trackEvent('appointment_created', { 
 *   appointment_type: 'checkup',
 *   provider_id: 'dr-smith'
 * });
 */
export function trackEvent(eventName, eventParams = {}) {
  // Check user consent
  const hasConsent = localStorage.getItem('analytics_consent') === 'true';
  if (!hasConsent) {
    logSecurityEvent('tracking_blocked_no_consent', { eventName });
    return;
  }
  
  if (typeof window.gtag !== 'undefined') {
    // Validate and sanitize event data
    const validation = validateEvent(eventName, eventParams);
    
    if (!validation?.safe) {
      logSecurityEvent('tracking_blocked_phi_detected', {
        eventName,
        reason: validation?.reason,
      });
      console.warn('[Analytics Security] Event blocked:', validation?.reason);
      return;
    }
    
    // Track with sanitized parameters
    window.gtag('event', eventName, {
      ...validation?.sanitizedParams,
      page: sanitizeURL(window.location?.pathname),
    });
    
    if (validation?.reason === 'Parameters sanitized') {
      logSecurityEvent('event_tracked_sanitized', { eventName });
    }
  }
}

/**
 * Helper function to track user actions (button clicks, form submissions, etc.)
 * 
 * @param {string} action - Action name (e.g., 'click', 'submit', 'download')
 * @param {string} category - Category of the action (e.g., 'Button', 'Form', 'Navigation')
 * @param {string} label - Label for the action (e.g., 'Schedule Demo', 'Login')
 * @param {number} value - Optional numeric value
 */
export function trackUserAction(action, category, label, value = null) {
  const eventParams = {
    event_category: category,
    event_label: label,
  };
  
  if (value !== null) {
    eventParams.value = value;
  }
  
  trackEvent(action, eventParams);
}

/**
 * Enables analytics tracking (user consent)
 */
export function enableAnalytics() {
  localStorage.setItem('analytics_consent', 'true');
  logSecurityEvent('consent_granted', { timestamp: new Date()?.toISOString() });
  
  // Reload page to initialize analytics
  window.location?.reload();
}

/**
 * Disables analytics tracking (user opt-out)
 */
export function disableAnalytics() {
  localStorage.setItem('analytics_consent', 'false');
  logSecurityEvent('consent_revoked', { timestamp: new Date()?.toISOString() });
  
  // Disable Google Analytics
  if (window.gtag) {
    const measurementId = import.meta.env?.VITE_GA_MEASUREMENT_ID;
    if (measurementId) {
      window[`ga-disable-${measurementId}`] = true;
    }
  }
  
  // Clear analytics cookies
  document.cookie?.split(';')?.forEach(cookie => {
    const name = cookie?.split('=')?.[0]?.trim();
    if (name?.startsWith('_ga') || name?.startsWith('_gid')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
}

/**
 * Checks if user has consented to analytics
 * @returns {boolean}
 */
export function hasAnalyticsConsent() {
  return localStorage.getItem('analytics_consent') === 'true';
}

export default useGoogleAnalytics;